import { FormSectionHandler } from '../entity.handler';
import { ExportTaskResponse, ExportTaskUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, CheckListField, ComboField, ComboValueHandlers, TextField, MemoField } from '../utils';

interface EditorValues {
    name?: string;
    client?: IdName;
    state?: string;
    deliveryMethod?: string;
    documentTypes?: string[];
    assets?: any[];
    endpointType?: string;
    endpoint?: string;
    prefix?: string;
    encoding?: string;
    parameters?: string;
    accessKey?: string;
}

export class ExportTaskSettingsSectionHandler implements FormSectionHandler<ExportTaskResponse, EditorValues> {

    type = 'form';
    icon = 'wrench';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    private properties = ['name', 'client', 'deliveryMethod', 'documentTypes', 'documentVersion', 'assets'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.EXPORTTASK.SETTINGS.${key}`, values);
    }

    applyUpdate(entity: ExportTaskResponse, payload: ExportTaskUpdateRequest): Promise<ExportTaskResponse> {
        return this.admin.applyUpdate(api => api.entities.updateExportTask(entity.id, payload));
    }

    async mapEntityToEditor(entity: ExportTaskResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        if (entity.settings) {
            result.endpointType = entity.settings.endpointType;
            result.endpoint = entity.settings.endpoint;
            result.prefix = entity.settings.prefix;
            result.encoding = entity.settings.encoding || 'gzip';
            result.parameters = entity.settings.parameters;
            result.accessKey = entity.settings.accessKey;
        }
        return result;
    }

    async mapEditorToEntity(entity: ExportTaskUpdateRequest, values: EditorValues): Promise<ExportTaskUpdateRequest> {
        const result = EntityUtils.mergeFields<ExportTaskUpdateRequest>(this.properties, values, entity);
        if (values.endpoint || values.endpointType) {

            if (values.endpointType === 'http') {
                if (!values.endpoint.startsWith('https://')) {
                    throw new Error('Only https:// endpoints are supported.');
                }
            }

            result.settings = result.settings || {};
            result.settings.endpointType = values.endpointType;
            result.settings.endpoint = values.endpoint;
            result.settings.prefix = values.prefix;
            result.settings.encoding = values.encoding;
            result.settings.parameters = values.parameters;
            result.settings.accessKey = values.accessKey;
        }
        return result;
    }

    async getFields(entity: ExportTaskResponse): Promise<FormBuilderField[]> {

        const documents = [
            { key: 'telemetry', value: this.admin.translate(`EDITORS.EXPORTTASK.DOCUMENT_TYPES.TELEMETRY`) },
            { key: 'event', value: this.admin.translate(`EDITORS.EXPORTTASK.DOCUMENT_TYPES.EVENT`) },
            { key: 'trip', value: this.admin.translate(`EDITORS.EXPORTTASK.DOCUMENT_TYPES.TRIP`) },
            { key: 'crashdata', value: this.admin.translate(`EDITORS.EXPORTTASK.DOCUMENT_TYPES.CRASH_DATA`) },
            { key: 'changes', value: this.admin.translate(`EDITORS.EXPORTTASK.DOCUMENT_TYPES.CHANGES`) },
        ];

        let fields: FormBuilderField[] = [
            { ...CheckListField('documentTypes', this.translate('FIELDS.DOCUMENTS'), true, documents), getText: (field, values) => (values[field.id] || []).map(x => documents.find(d => d.key === x).value).join(', ') },
            ComboField('documentVersion', this.translate('FIELDS.DOCUMENT_VERSION'), true, [
                { key: 'v1', value: 'v1 (Legacy)' },
                { key: 'v2', value: 'v2 (Latest)' },
            ], ComboValueHandlers),
            entity && entity.client && entity.deliveryMethod !== 'firehose' && {
                type: 'assetfilter', id: 'assets', title: this.translate('FIELDS.ASSETS'), 'required': true,
                options: { ownerId: entity.client.id },
                getText: (field, values) => (values[field.id] || []).map(x => x.text).join(', '),
            },
            entity && entity.deliveryMethod === 'firehose' && {
                type: 'clientfilter', id: 'assets', title: this.translate('FIELDS.CLIENTS'), 'required': true,
                options: { ownerId: this.ownerId },
                getText: (field, values) => (values[field.id] || []).map(x => x.text).join(', '),
            },
        ].filter(x => x);

        if (entity?.deliveryMethod === 'firehose') {

            const editable = this.creating || entity?.state !== 'active';

            const modes = [
                {
                    key: 's3', value: this.admin.translate(`EDITORS.EXPORTTASK.ENDPOINT_TYPES.S3`), fields: [
                        { ...TextField('endpoint', this.translate('FIELDS.BUCKET_ARN'), true, 1, 255), readonly: !editable, description: this.translate('FIELDS.BUCKET_ARN_DESC') },
                        { ...TextField('prefix', this.translate('FIELDS.PREFIX'), false, 1, 255), readonly: !editable },
                    ],
                },
                {
                    key: 'http', value: this.admin.translate(`EDITORS.EXPORTTASK.ENDPOINT_TYPES.HTTP`), fields: [
                        { ...TextField('endpoint', this.translate('FIELDS.URL'), true, 1, 255), readonly: !editable, description: this.translate('FIELDS.URL_DESC') },
                        { ...TextField('accessKey', this.translate('FIELDS.ACCESS_KEY'), false, 1, 255), readonly: !editable, description: this.translate('FIELDS.ACCESS_KEY_DESC'), type: 'password' },
                        { ...ComboField('encoding', this.translate('FIELDS.ENCODING'), true, [{ key: 'none', value: 'Disabled' }, { key: 'gzip', value: 'GZIP' }], ComboValueHandlers), readonly: !editable },
                        { ...MemoField('parameters', this.translate('FIELDS.PARAMS'), false, 0, 2048), readonly: !editable, description: this.translate('FIELDS.PARAMS_DESC') },
                    ],
                },
            ].filter(x => x);
            fields = [
                ...fields,
                { ...ComboField('endpointType', this.translate('FIELDS.ENDPOINT_TYPE'), true, modes, ComboValueHandlers), readonly: !editable },
            ];
        }

        return EntityUtils.prepareBulk(!entity, [], fields);
    }




}
